import { ArrowUturnLeftIcon, ArrowUturnRightIcon, TrashIcon } from '@heroicons/react/24/outline';
import { markupHint } from './markupHints';
import type { MarkupShape, MarkupTool } from './markupShapes';
import { isMacPlatform } from './markupShortcuts';
import type { MarkupSize } from './markupStyle';
import { BarDivider, ColorSwatches, IconAction, SizePicker } from './MarkupToolbarControls';

interface MarkupTopBarProps {
    tool: MarkupTool;
    selectedKind: MarkupShape['kind'] | null;
    color: string;
    onColorChange: (color: string) => void;
    size: MarkupSize;
    onSizeChange: (size: MarkupSize) => void;
    history: { canUndo: boolean; canRedo: boolean; canClear: boolean };
    onUndo: () => void;
    onRedo: () => void;
    onClear: () => void;
}

const IS_MAC = isMacPlatform();
const UNDO_SHORTCUT = IS_MAC ? '⌘Z' : 'Ctrl+Z';
const REDO_SHORTCUT = IS_MAC ? '⇧⌘Z' : 'Ctrl+Y';
const ICON = 'w-5 h-5';

function MarkupTopBar({
    tool,
    selectedKind,
    color,
    onColorChange,
    size,
    onSizeChange,
    history,
    onUndo,
    onRedo,
    onClear
}: MarkupTopBarProps) {
    return (
        <div className="flex items-center gap-4 h-14 px-4 border-b border-gray-200 bg-white">
            <ColorSwatches value={color} onChange={onColorChange} />
            <BarDivider />
            <SizePicker value={size} onChange={onSizeChange} />
            <p aria-live="polite" className="flex-1 min-w-0 truncate text-xs text-gray-500">
                {markupHint(tool, selectedKind)}
            </p>
            <div className="flex items-center gap-1">
                <IconAction
                    label="Undo"
                    tooltip={`Undo (${UNDO_SHORTCUT})`}
                    disabled={!history.canUndo}
                    onClick={onUndo}
                >
                    <ArrowUturnLeftIcon className={ICON} aria-hidden="true" />
                </IconAction>
                <IconAction
                    label="Redo"
                    tooltip={`Redo (${REDO_SHORTCUT})`}
                    disabled={!history.canRedo}
                    onClick={onRedo}
                >
                    <ArrowUturnRightIcon className={ICON} aria-hidden="true" />
                </IconAction>
                <span className="mx-1">
                    <BarDivider />
                </span>
                <IconAction
                    label="Clear all marks"
                    tooltip="Clear all marks (undoable)"
                    disabled={!history.canClear}
                    onClick={onClear}
                >
                    <TrashIcon className={ICON} aria-hidden="true" />
                </IconAction>
            </div>
        </div>
    );
}

export { MarkupTopBar };
